import { extendTheme, type ThemeConfig } from '@chakra-ui/react'

const fonts = {
  body: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  heading: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
}

const landingConfig = {
  colors: {
    blue: {
      50: '#f3f8ff',
      100: '#e9f1ff',
      200: '#c8d7ff',
      300: '#a6bcff',
      400: '#638aff',
      500: '#2563eb',
      600: '#1e4ab3'
    }
  },
  fonts,
  styles: { global: { html: { scrollBehavior: 'smooth' } } }
}

const internalComponents = {
  Button: {
    baseStyle: { borderRadius: '10px', fontWeight: 600, boxShadow: 'none' },
    sizes: {
      md: { h: '36px', minW: '36px', px: 4, fontSize: 'sm' },
      lg: { h: '40px', minW: '40px', px: 5, fontSize: 'sm' }
    },
    variants: {
      solid: {
        bg: 'gray.900',
        color: 'white',
        _hover: { bg: 'black', _disabled: { bg: 'gray.200' } },
        _active: { bg: 'black' }
      },
      outline: {
        bg: 'white',
        color: 'gray.800',
        borderColor: 'gray.200',
        _hover: { bg: 'gray.50', borderColor: 'gray.300' }
      },
      ghost: {
        color: 'gray.700',
        _hover: { bg: 'gray.100', color: 'gray.950' }
      }
    },
    defaultProps: { colorScheme: 'gray' }
  },
  Input: {
    sizes: {
      md: { field: { h: '36px', borderRadius: '10px', fontSize: 'sm' } }
    },
    variants: {
      outline: {
        field: {
          bg: 'white',
          borderColor: 'gray.200',
          _hover: { borderColor: 'gray.300' },
          _focusVisible: {
            borderColor: 'gray.900',
            boxShadow: '0 0 0 1px #171717'
          }
        }
      },
      filled: {
        field: {
          bg: 'gray.50',
          border: '1px solid',
          borderColor: 'gray.200',
          _hover: { bg: 'gray.50', borderColor: 'gray.300' },
          _focusVisible: { bg: 'white', borderColor: 'gray.900' }
        }
      }
    },
    defaultProps: { variant: 'outline', size: 'md' }
  },
  Select: {
    sizes: {
      md: { field: { h: '36px', borderRadius: '10px', fontSize: 'sm' } }
    },
    variants: {
      outline: {
        field: {
          bg: 'white',
          borderColor: 'gray.200',
          _hover: { borderColor: 'gray.300' },
          _focusVisible: {
            borderColor: 'gray.900',
            boxShadow: '0 0 0 1px #171717'
          }
        }
      }
    },
    defaultProps: { variant: 'outline', size: 'md' }
  },
  Textarea: {
    variants: {
      outline: {
        bg: 'white',
        borderColor: 'gray.200',
        borderRadius: '10px',
        _hover: { borderColor: 'gray.300' },
        _focusVisible: {
          borderColor: 'gray.900',
          boxShadow: '0 0 0 1px #171717'
        }
      }
    },
    defaultProps: { variant: 'outline' }
  },
  Card: {
    baseStyle: {
      container: {
        borderRadius: '10px',
        borderWidth: '1px',
        borderColor: 'gray.200',
        boxShadow: '0 1px 2px rgba(0,0,0,.03)',
        bg: 'white'
      }
    }
  },
  Modal: {
    baseStyle: {
      dialog: {
        borderRadius: '12px',
        boxShadow: '0 16px 48px rgba(0,0,0,.14)'
      },
      header: { fontSize: 'lg', fontWeight: 650 },
      overlay: { bg: 'blackAlpha.400' }
    }
  },
  Table: {
    baseStyle: {
      th: {
        color: 'gray.500',
        fontSize: 'xs',
        letterSpacing: 'normal',
        textTransform: 'none',
        borderColor: 'gray.200'
      },
      td: { borderColor: 'gray.100', fontSize: 'sm' }
    }
  },
  Tabs: {
    variants: {
      line: {
        tab: {
          color: 'gray.500',
          _selected: { color: 'gray.950', borderColor: 'gray.950' }
        },
        tablist: { borderColor: 'gray.200' }
      }
    }
  },
  Badge: {
    baseStyle: {
      borderRadius: '6px',
      px: 2,
      py: 0.5,
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: 'normal'
    }
  },
  Menu: {
    baseStyle: {
      list: {
        borderRadius: '10px',
        borderColor: 'gray.200',
        boxShadow: '0 12px 32px rgba(0,0,0,.10)',
        p: 1
      },
      item: { borderRadius: '7px', fontSize: 'sm', _focus: { bg: 'gray.100' } }
    }
  },
  Alert: {
    baseStyle: { container: { borderRadius: '10px', borderWidth: '1px' } }
  },
  FormLabel: {
    baseStyle: { fontSize: 'sm', color: 'gray.700', fontWeight: 600, mb: 1.5 }
  }
}

const internalConfig: ThemeConfig = {
  initialColorMode: 'light',
  useSystemColorMode: false
}

const makeInternalTheme = (direction: 'ltr' | 'rtl') =>
  extendTheme({
    config: internalConfig,
    direction,
    fonts,
    radii: { md: '10px', lg: '10px', xl: '12px', '2xl': '14px' },
    shadows: {
      sm: '0 1px 2px rgba(0,0,0,.04)',
      md: '0 6px 18px rgba(0,0,0,.07)',
      lg: '0 16px 40px rgba(0,0,0,.10)'
    },
    colors: {
      blue: {
        50: '#f5f5f5',
        100: '#e5e5e5',
        200: '#d4d4d4',
        300: '#a3a3a3',
        400: '#737373',
        500: '#262626',
        600: '#171717',
        700: '#0a0a0a',
        800: '#0a0a0a',
        900: '#000000'
      },
      primary: '#171717'
    },
    fontSizes: {
      xs: '0.75rem',
      sm: '0.8125rem',
      md: '0.875rem',
      lg: '1rem',
      xl: '1.125rem',
      '2xl': '1.375rem'
    },
    components: internalComponents,
    styles: {
      global: {
        'html, body, #root': { minHeight: '100%' },
        html: { scrollBehavior: 'smooth' },
        body: { bg: '#fafafa', color: '#171717', fontSize: '14px' },
        '::selection': { bg: '#e5e5e5' },
        '*:focus-visible': { outlineColor: '#171717' }
      }
    }
  })

export const getTheme = (direction: 'ltr' | 'rtl', internal = false) =>
  internal
    ? makeInternalTheme(direction)
    : extendTheme({ ...landingConfig, direction })

export default getTheme('ltr')
